import { useState } from 'react';
import { useCreateAssessment, useAllTeamUsers } from '@/api/supervisor';
import { Combobox } from '@/components/shared/combobox';
import { X } from 'lucide-react';

// Diálogo modal para crear una evaluación de competencia (con criterios).
// Auto-contenido. Extraído de assessments.tsx por ENG-LOC-01.
export function CreateAssessmentDialog({ onClose }: { onClose: () => void }) {
  const [assessedId, setAssessedId] = useState<number>(0);
  const [assessmentType, setAssessmentType] = useState('probation');
  const [criteria, setCriteria] = useState([{ name: '', score: 0, max_score: 100, comments: '' }]);

  const { data: teamData, isLoading: loadingTeam } = useAllTeamUsers();
  const createMutation = useCreateAssessment();

  const teamUsers = teamData?.data ?? [];

  function addCriterion() {
    setCriteria([...criteria, { name: '', score: 0, max_score: 100, comments: '' }]);
  }

  function removeCriterion(index: number) {
    setCriteria(criteria.filter((_, i) => i !== index));
  }

  function updateCriterion(index: number, field: string, value: string | number) {
    setCriteria(criteria.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!assessedId || criteria.some((c) => !c.name.trim())) return;

    createMutation.mutate(
      {
        assessed_id: assessedId,
        assessment_type: assessmentType,
        criteria: criteria.map((c) => ({
          name: c.name,
          score: Number(c.score),
          max_score: Number(c.max_score),
          ...(c.comments ? { comments: c.comments } : {}),
        })),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-background p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Crear Evaluación</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User select */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Empleado</label>
            {loadingTeam ? (
              <p className="text-sm text-muted-foreground">Cargando...</p>
            ) : (
              <Combobox
                value={assessedId ? String(assessedId) : ''}
                onChange={(v) => setAssessedId(v ? Number(v) : 0)}
                options={teamUsers.map((u) => ({
                  value: String(u.id),
                  label: `${u.first_name} ${u.last_name}`,
                }))}
                placeholder="Seleccionar empleado..."
                clearable
              />
            )}
          </div>

          {/* Type select */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Tipo</label>
            <select
              value={assessmentType}
              onChange={(e) => setAssessmentType(e.target.value)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground"
            >
              <option value="probation">Período de prueba</option>
              <option value="periodic">Periódica</option>
              <option value="competency_check">Verificacion de competencia</option>
            </select>
          </div>

          {/* Criteria */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Criterios</label>
              <button
                type="button"
                onClick={addCriterion}
                className="text-xs text-primary hover:underline"
              >
                + Agregar criterio
              </button>
            </div>
            <div className="space-y-3">
              {criteria.map((c, i) => (
                <div key={i} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Nombre del criterio"
                      value={c.name}
                      onChange={(e) => updateCriterion(i, 'name', e.target.value)}
                      required
                      className="flex-1 rounded border border-border bg-card px-2 py-1 text-sm text-foreground"
                    />
                    {criteria.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCriterion(i)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Puntuacion</label>
                      <input
                        type="number"
                        min={0}
                        max={c.max_score}
                        value={c.score}
                        onChange={(e) => updateCriterion(i, 'score', Number(e.target.value))}
                        className="w-full rounded border border-border bg-card px-2 py-1 text-sm text-foreground"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Maximo</label>
                      <input
                        type="number"
                        min={1}
                        value={c.max_score}
                        onChange={(e) => updateCriterion(i, 'max_score', Number(e.target.value))}
                        className="w-full rounded border border-border bg-card px-2 py-1 text-sm text-foreground"
                      />
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Comentarios (opcional)"
                    value={c.comments}
                    onChange={(e) => updateCriterion(i, 'comments', e.target.value)}
                    className="w-full rounded border border-border bg-card px-2 py-1 text-sm text-foreground"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !assessedId}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Evaluación'}
            </button>
          </div>

          {createMutation.isError && (
            <p className="text-sm text-destructive">Error al crear la evaluación.</p>
          )}
        </form>
      </div>
    </div>
  );
}
